(ns hai.home.progress
  "Shared timeline math for the home Progress mini-app: the day-window that spans
   at least 8am–6pm (expanded to fit meetings and completed tasks), its 2-hour
   axis ticks and labels, the per-item positions along the window, and the
   burst-clustering of completion stamps. The desktop card lays these out along
   a horizontal axis and the mobile card along a vertical one, so only the math
   is shared — each surface owns its own rendering."
  (:require
   [hai.home.calendar :as calendar]))

(defn compute-window
  "Day window {:start-ms :end-ms :total-ms :start-hour :end-hour} covering at
   least 8am–6pm, expanded to fit any meetings or completions outside that band,
   rounded to hour boundaries."
  [meetings completions]
  (let [finite-ms     (fn [xs] (filter #(and (number? %) (js/isFinite %)) xs))
        today-ms      (.getTime (doto (js/Date.) (.setHours 0 0 0 0)))
        hour-ms       (* 60 60 1000)
        default-start (+ today-ms (* 8 hour-ms))
        default-end   (+ today-ms (* 18 hour-ms))
        earliest-ms   (when (seq meetings)
                        (.getTime (js/Date. (:journal.entry/time (first meetings)))))
        ;; Meetings are sorted by start, so the latest END can belong to any of
        ;; them — a long meeting can outlast every later-starting one.
        latest-ms     (when (seq meetings)
                        (apply max
                               (map (fn [meeting]
                                      (.getTime (js/Date. (or (calendar/m-end-at meeting)
                                                              (:journal.entry/time meeting)))))
                                    meetings)))
        first-done-ms (when (seq completions)
                        (.getTime (js/Date. (:task/completed-at (first completions)))))
        last-done-ms  (when (seq completions)
                        (.getTime (js/Date. (:task/completed-at (last completions)))))
        win-start-ms  (let [ms (apply min (finite-ms [default-start earliest-ms first-done-ms]))
                            d  (js/Date. ms)]
                        (.setMinutes d 0 0 0)
                        (.getTime d))
        win-end-ms    (let [ms (apply max (finite-ms [default-end latest-ms last-done-ms]))
                            d  (js/Date. ms)]
                        (when (pos? (.getMinutes d))
                          (.setHours d (inc (.getHours d)) 0 0 0))
                        (.getTime d))]
    {:start-ms   win-start-ms
     :end-ms     win-end-ms
     :total-ms   (- win-end-ms win-start-ms)
     :start-hour (/ (- win-start-ms today-ms) hour-ms)
     :end-hour   (/ (- win-end-ms today-ms) hour-ms)}))

(defn axis-hours
  "Hour numbers for the axis ticks, every 2 hours across the window."
  [{:keys [start-hour end-hour]}]
  ;; A non-finite bound (a NaN from an Invalid Date upstream) would make the
  ;; `(> h end-hour)` test never hold — every comparison with NaN is false — so
  ;; the loop would spin forever and peg the tab. Bail to no ticks instead.
  (if (and (js/isFinite start-hour) (js/isFinite end-hour))
    (loop [h start-hour acc []]
      (if (> h end-hour)
        acc
        (recur (+ h 2) (conj acc h))))
    []))

(defn meeting-span-pct
  "A meeting's span along the window's time axis, {:offset-pct :extent-pct}
   percentages of the window (a missing end defaults to 30 minutes), clamped to
   the window's bounds; nil when the meeting lies entirely outside it."
  [meeting {:keys [start-ms end-ms total-ms]}]
  (let [start-raw (.getTime (js/Date. (:journal.entry/time meeting)))
        end-raw   (if-let [end-at (calendar/m-end-at meeting)]
                    (.getTime (js/Date. end-at))
                    (+ start-raw (* 30 60000)))
        start     (max start-raw start-ms)
        end       (min end-raw end-ms)]
    (when (< start end)
      {:offset-pct (* 100 (/ (- start start-ms) total-ms))
       :extent-pct (* 100 (/ (- end start) total-ms))})))

(defn now-pct
  "Now's position along the window as a percentage; nil when outside it."
  [now-ms {:keys [start-ms end-ms total-ms]}]
  (when (and now-ms (>= now-ms start-ms) (<= now-ms end-ms))
    (* 100 (/ (- now-ms start-ms) total-ms))))

(defn stamp-pct
  "A completion cluster's position along the window: the percent at `mean-ms`,
   clamped into [0 100] so edge bursts stay on the strip."
  [mean-ms {:keys [start-ms total-ms]}]
  (-> (* 100 (/ (- mean-ms start-ms) total-ms))
      (max 0)
      (min 100)))

(def ^:private stamp-cluster-gap-ms (* 36 60000))

(defn cluster-completions
  "Burst grouping for the stamp layer: a completion joins the open group when it
   lands within 36 minutes of the group's latest member, otherwise it starts a
   new group. Each group carries its `:tasks` and the `:mean-ms` completion time
   for positioning."
  [completed-tasks]
  (->> completed-tasks
       (map (fn [task]
              (assoc task ::completed-ms
                     (.getTime (js/Date. (:task/completed-at task))))))
       (reduce (fn [groups task]
                 (let [open-group (peek groups)
                       latest-ms  (some-> open-group peek ::completed-ms)]
                   (if (and latest-ms
                            (<= (- (::completed-ms task) latest-ms)
                                stamp-cluster-gap-ms))
                     (conj (pop groups) (conj open-group task))
                     (conj groups [task]))))
               [])
       (mapv (fn [group]
               {:tasks   group
                :mean-ms (/ (transduce (map ::completed-ms) + group)
                            (count group))}))))
